import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  User, LogOut, Menu, X, Crown, Settings, ChevronDown, 
  FileSearch, BookOpen, MessageCircle, Camera,
  Home, History
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const { user, logout, isPremium } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // V1 Simplified navigation - All main links for logged users
  const mainNavLinks = user ? [
    { to: '/dashboard', label: 'Dashboard', icon: Home },
    { to: '/historique', label: 'Historique', icon: History },
    { to: '/analyze', label: 'Analyser un devis', icon: FileSearch },
    { to: '/recipes', label: 'Recette travaux', icon: BookOpen },
    { to: '/diagnostic', label: 'Diagnostic', icon: Camera },
    { to: '/chat', label: 'Poser une question', icon: MessageCircle },
  ] : [];

  // Mobile menu links (4 piliers)
  const mobileMenuLinks = [
    { to: '/analyze', label: 'Analyser un devis', icon: FileSearch, color: 'blue' },
    { to: '/recipes', label: 'Recette travaux', icon: BookOpen, color: 'emerald' },
    { to: '/diagnostic', label: 'Diagnostic photo', icon: Camera, color: 'purple' },
    { to: '/chat', label: 'Assistant', icon: MessageCircle, color: 'gold' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Brand - Toujours vers la page d'accueil */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img 
              src="/assets/phenix-logo.png" 
              alt="Assistant by Léon" 
              className="h-8 w-auto transition-transform group-hover:scale-105"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="hidden sm:flex flex-col">
              <span className="text-base font-heading font-bold text-gold-500 leading-tight">
                Assistant by Léon
              </span>
              <span className="text-[10px] text-zinc-500 leading-tight">
                Copilote intelligent de vos travaux
              </span>
            </div>
          </Link>

          {/* Desktop Navigation - All main links */}
          {user && (
            <div className="hidden lg:flex items-center gap-1">
              {mainNavLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    isActive(link.to)
                      ? 'bg-zinc-800 text-white'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <link.icon className="w-3.5 h-3.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {/* Premium Badge or CTA */}
                {isPremium ? (
                  <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gold-500/10 rounded-full border border-gold-500/20">
                    <Crown className="w-4 h-4 text-gold-500" />
                    <span className="text-gold-400 text-xs font-medium">Premium</span>
                  </div>
                ) : (
                  <Link
                    to="/subscription"
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gold-500 hover:bg-gold-400 rounded-full text-black text-xs font-bold transition-colors"
                  >
                    <Crown className="w-4 h-4" />
                    Premium
                  </Link>
                )}

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-1.5 rounded-full hover:bg-zinc-800 transition-colors"
                    data-testid="user-menu-btn"
                  >
                    {user.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name || 'Utilisateur'} 
                        className="w-8 h-8 rounded-full object-cover border-2 border-zinc-700"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center border-2 border-zinc-700">
                        <User className="w-4 h-4 text-zinc-400" />
                      </div>
                    )}
                    <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl overflow-hidden"
                      >
                        {/* User info */}
                        <div className="px-4 py-3 border-b border-zinc-800">
                          <p className="text-sm font-medium text-white truncate">{user.name || 'Utilisateur'}</p>
                          <p className="text-xs text-zinc-500 truncate">{user.email}</p>
                        </div>

                        {/* Menu items */}
                        <div className="py-1">
                          <Link
                            to="/account"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors"
                          >
                            <User className="w-4 h-4" />
                            Espace client
                          </Link>
                          
                          {!isPremium && (
                            <Link
                              to="/subscription"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gold-400 hover:bg-gold-500/10 transition-colors"
                            >
                              <Crown className="w-4 h-4" />
                              Passer Premium
                            </Link>
                          )}
                        </div>

                        {/* Logout */}
                        <div className="border-t border-zinc-800 py-1">
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <LogOut className="w-4 h-4" />
                            Déconnexion
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile menu button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800/50 transition-colors"
                  data-testid="mobile-menu-btn"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </>
            ) : (
              <Link
                to="/auth"
                className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-black text-sm font-bold rounded-lg transition-colors"
                data-testid="login-btn"
              >
                Se connecter
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu - Full Screen Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && user && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 top-16 bg-black overflow-y-auto"
              style={{ zIndex: 9999 }}
            >
              <div className="p-4 space-y-1 max-w-lg mx-auto">
                {/* User Info Card */}
                <div className="flex items-center gap-3 p-4 bg-zinc-900/50 rounded-xl mb-4">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gold-500/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-gold-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{user.name || 'Utilisateur'}</p>
                    <p className="text-zinc-500 text-sm truncate">{user.email}</p>
                  </div>
                  {isPremium && (
                    <span className="px-2 py-1 bg-gold-500/20 text-gold-500 text-xs font-bold rounded-full">
                      Premium
                    </span>
                  )}
                </div>

                {/* Navigation principale */}
                <div className="space-y-1">
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-colors ${
                      isActive('/dashboard') 
                        ? 'bg-gold-500/20 text-gold-400' 
                        : 'text-zinc-300 hover:bg-zinc-800/50'
                    }`}
                  >
                    <Home className="w-5 h-5" />
                    <span className="font-medium">Dashboard</span>
                  </Link>

                  <Link
                    to="/historique"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-colors ${
                      isActive('/historique') 
                        ? 'bg-gold-500/20 text-gold-400' 
                        : 'text-zinc-300 hover:bg-zinc-800/50'
                    }`}
                  >
                    <History className="w-5 h-5" />
                    <span className="font-medium">Historique</span>
                  </Link>
                </div>

                {/* Séparateur */}
                <div className="py-3">
                  <div className="h-px bg-zinc-800" />
                  <p className="text-xs text-zinc-600 uppercase font-medium mt-3 mb-2 px-4">
                    Les 4 piliers
                  </p>
                </div>

                {/* 4 Piliers avec couleurs */}
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/analyze"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${
                      isActive('/analyze') 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : 'bg-zinc-900/50 text-zinc-300 hover:bg-zinc-800/50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isActive('/analyze') ? 'bg-blue-500/30' : 'bg-blue-500/10'
                    }`}>
                      <FileSearch className="w-6 h-6 text-blue-400" />
                    </div>
                    <span className="text-sm font-medium text-center">Analyser un devis</span>
                  </Link>

                  <Link
                    to="/recipes"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${
                      isActive('/recipes') 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-zinc-900/50 text-zinc-300 hover:bg-zinc-800/50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isActive('/recipes') ? 'bg-emerald-500/30' : 'bg-emerald-500/10'
                    }`}>
                      <BookOpen className="w-6 h-6 text-emerald-400" />
                    </div>
                    <span className="text-sm font-medium text-center">Recette travaux</span>
                  </Link>

                  <Link
                    to="/diagnostic"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${
                      isActive('/diagnostic') 
                        ? 'bg-purple-500/20 text-purple-400' 
                        : 'bg-zinc-900/50 text-zinc-300 hover:bg-zinc-800/50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isActive('/diagnostic') ? 'bg-purple-500/30' : 'bg-purple-500/10'
                    }`}>
                      <Camera className="w-6 h-6 text-purple-400" />
                    </div>
                    <span className="text-sm font-medium text-center">Diagnostic photo</span>
                  </Link>

                  <Link
                    to="/chat"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${
                      isActive('/chat') 
                        ? 'bg-gold-500/20 text-gold-400' 
                        : 'bg-zinc-900/50 text-zinc-300 hover:bg-zinc-800/50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isActive('/chat') ? 'bg-gold-500/30' : 'bg-gold-500/10'
                    }`}>
                      <MessageCircle className="w-6 h-6 text-gold-400" />
                    </div>
                    <span className="text-sm font-medium text-center">Poser une question</span>
                  </Link>
                </div>

                {/* Séparateur */}
                <div className="py-3">
                  <div className="h-px bg-zinc-800" />
                  <p className="text-xs text-zinc-600 uppercase font-medium mt-3 mb-2 px-4">
                    Mon compte
                  </p>
                </div>

                {/* Espace client */}
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-colors ${
                    isActive('/account') 
                      ? 'bg-gold-500/20 text-gold-400' 
                      : 'text-zinc-300 hover:bg-zinc-800/50'
                  }`}
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Espace client</span>
                </Link>

                {/* Premium CTA */}
                {!isPremium && (
                  <Link
                    to="/subscription"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 px-4 py-3.5 bg-gradient-to-r from-gold-500 to-amber-500 text-black font-bold rounded-xl mt-2"
                  >
                    <Crown className="w-5 h-5" />
                    Passer Premium
                  </Link>
                )}

                {/* Déconnexion */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors mt-4"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Déconnexion</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Mobile Menu - OUTSIDE max-w-7xl container */}
      <AnimatePresence>
        {mobileMenuOpen && user && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden absolute left-0 right-0 top-16 bottom-auto bg-black border-b border-zinc-800 shadow-2xl"
            style={{ zIndex: 100 }}
          >
            <div className="p-4 space-y-2 max-h-[calc(100vh-4rem)] overflow-y-auto">
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-xl mb-3">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-gold-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{user.name || 'Utilisateur'}</p>
                  <p className="text-zinc-500 text-xs truncate">{user.email}</p>
                </div>
                {isPremium && (
                  <span className="px-2 py-0.5 bg-gold-500/20 text-gold-500 text-xs font-bold rounded-full">
                    Premium
                  </span>
                )}
              </div>

              {/* Quick links */}
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                  isActive('/dashboard') ? 'bg-gold-500/20 text-gold-400' : 'text-zinc-300'
                }`}
              >
                <Home className="w-4 h-4" />
                Dashboard
              </Link>
              <Link
                to="/historique"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                  isActive('/historique') ? 'bg-gold-500/20 text-gold-400' : 'text-zinc-300'
                }`}
              >
                <History className="w-4 h-4" />
                Historique
              </Link>

              {/* 4 Piliers */}
              <div className="pt-2 pb-1">
                <p className="text-xs text-zinc-600 uppercase px-3">Les 4 piliers</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to="/analyze"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-zinc-900/50"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <FileSearch className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-xs text-zinc-300 text-center">Analyser devis</span>
                </Link>
                <Link
                  to="/recipes"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-zinc-900/50"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-xs text-zinc-300 text-center">Recette travaux</span>
                </Link>
                <Link
                  to="/diagnostic"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-zinc-900/50"
                >
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Camera className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-xs text-zinc-300 text-center">Diagnostic</span>
                </Link>
                <Link
                  to="/chat"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-zinc-900/50"
                >
                  <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-gold-400" />
                  </div>
                  <span className="text-xs text-zinc-300 text-center">Question</span>
                </Link>
              </div>

              {/* Compte */}
              <div className="pt-2 flex gap-2">
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-zinc-800 text-zinc-300 text-sm"
                >
                  <User className="w-4 h-4" />
                  Espace client
                </Link>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2.5 rounded-lg bg-red-500/10 text-red-400"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
